<template>
  <div class="bank-picker" :class="{ compact }">
    <div v-if="showTitle" class="picker-head">
      <div class="head-icon">🎯</div>
      <h3>{{ title }}</h3>
      <p v-if="subtitle" class="picker-sub">{{ subtitle }}</p>
    </div>
    <div class="banks-list">
      <div
        v-for="(bank, index) in banks"
        :key="bank.id"
        class="bank-card"
        :class="{ active: selectedId === bank.id, blocked: bank.purchase_blocked, 'has-access': bank.has_access }"
        :style="{ animationDelay: index * 0.1 + 's' }"
        @click="onPick(bank)"
      >
        <div class="card-glow"></div>
        <div class="bank-card-icon">
          <div class="icon-bg" :class="{ unlocked: bank.has_access }">
            {{ bank.icon || '📚' }}
          </div>
        </div>
        <div class="bank-card-body">
          <div class="bank-card-name">{{ bank.name }}</div>
          <div class="bank-card-meta">
            <span class="question-count">{{ bank.question_count }} 道精选题目</span>
            <span v-if="bank.has_access" class="status-tag access">
              <van-icon name="check" size="12" />
              {{ bankAccessLabel(bank) }}
            </span>
            <span v-else-if="bank.purchase_blocked" class="status-tag blocked">
              <van-icon name="warning-o" size="12" />
              已开通更高等级
            </span>
            <span v-else class="status-tag locked">
              <van-icon name="lock" size="12" />
              未开通
            </span>
          </div>
        </div>
        <div class="bank-card-arrow">
          <van-icon :name="iconFor(bank)" size="20" />
        </div>
      </div>
    </div>
    <van-empty v-if="!loading && banks.length === 0" description="暂无题库，请联系管理员" />
  </div>
</template>

<script>
import { bankAccessStatusText } from '@/utils/vipDisplay'

export default {
  name: 'BankPicker',
  props: {
    banks: { type: Array, default: () => [] },
    selectedId: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    title: { type: String, default: '选择题库' },
    subtitle: { type: String, default: '答题与开通会员均按题库独立计算' },
    showTitle: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    mode: { type: String, default: 'quiz' }
  },
  methods: {
    bankAccessLabel(bank) {
      return bankAccessStatusText(bank)
    },
    iconFor(bank) {
      if (this.mode === 'vip' && bank.purchase_blocked) return 'warning-o'
      if (bank.has_access) return 'arrow'
      return 'lock'
    },
    onPick(bank) {
      this.$emit('select', bank)
    }
  }
}
</script>

<style scoped>
.bank-picker {
  padding: 0 16px;
}

.picker-head {
  text-align: center;
  padding: 32px 0 28px;
}

.head-icon {
  font-size: 48px;
  margin-bottom: 16px;
  animation: icon-bounce 2s ease-in-out infinite;
}

@keyframes icon-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.picker-head h3 {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.picker-sub {
  font-size: 14px;
  color: #888;
  margin: 0;
  line-height: 1.6;
}

.banks-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bank-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.08);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: translateY(20px);
  animation: card-fade-in 0.5s ease-out forwards;
}

@keyframes card-fade-in {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bank-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15);
}

.bank-card:active {
  transform: translateY(-2px);
}

.bank-card.active {
  border-color: #667eea;
  background: linear-gradient(135deg, #faf5ff 0%, #f0f5ff 100%);
}

.bank-card.blocked {
  opacity: 0.7;
}

.bank-card.has-access {
  border-color: rgba(82, 196, 26, 0.3);
}

.card-glow {
  position: absolute;
  top: -30%;
  right: -10%;
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.05) 0%, transparent 70%);
  border-radius: 50%;
  transition: all 0.4s ease;
}

.bank-card:hover .card-glow {
  transform: scale(1.3);
  background: radial-gradient(circle, rgba(102, 126, 234, 0.08) 0%, transparent 70%);
}

.bank-card-icon {
  flex-shrink: 0;
}

.icon-bg {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f0ff 0%, #e8e8ff 100%);
  border-radius: 16px;
  font-size: 32px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
}

.icon-bg.unlocked {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  box-shadow: 0 4px 16px rgba(82, 196, 26, 0.15);
}

.bank-card:hover .icon-bg {
  transform: scale(1.1);
}

.bank-card-body {
  flex: 1;
}

.bank-card-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.bank-card-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.question-count {
  font-size: 14px;
  color: #999;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-tag.access {
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  color: #52c41a;
}

.status-tag.blocked {
  background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
  color: #fa8c16;
}

.status-tag.locked {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  color: #999;
}

.bank-card-arrow {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.bank-card.has-access .bank-card-arrow {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.bank-card:hover .bank-card-arrow {
  transform: translateX(4px);
}
</style>
